import dynamic from 'next/dynamic';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { twJoin, twMerge } from 'tailwind-merge';

import Anchor from 'components/Anchor';
import Image from 'components/Image';

import useModal from 'hooks/useModal';

import { getAdCssBackgroundColorProp } from 'lib/ads';
import { sendEvent } from 'lib/gtag';
import { isUrlValidForInternalUsage } from 'lib/string';

import APP from 'constants/app';

const WishlistModal = dynamic(
  () => import('components/Wishlist/WishlistModal'),
  { ssr: false }
);

const FeaturedImage = ({
  bgcolor = ['#ffffff', '#ffffff'],
  className,
  description = null,
  gaEventCategory = null,
  image,
  mobileImage = null,
  url = null,
  openWishlistOnClick = false,
}) => {
  const { showModal } = useModal();

  if (openWishlistOnClick) {
    showModal(WishlistModal);
  }

  const onImageClick = () => {
    if (!gaEventCategory) {
      return;
    }

    sendEvent({ action: 'featured_image_click', category: gaEventCategory });
  };

  const ImageComponent = (
    <Image
      alt={`banner da ${description}`}
      className={twJoin(
        'object-cover md:h-[286px] md:w-full',
        'mx-auto w-screen-sm items-center justify-center lg:w-[1264px]',
        mobileImage ? 'flex' : 'hidden lg:flex'
      )}
      height={185}
      loading={null}
      sizes={
        mobileImage
          ? '(max-width: 768px) 1px, 1264px'
          : '(max-width: 1024px) 0px, 1264px'
      }
      src={image}
      srcSet={
        mobileImage
          ? `${mobileImage} 360w, ${image} 1264w`
          : `${APP.LOCAL_IMAGE_PATH}/general/placeholder.jpg 1w, ${image} 1264w`
      }
      width={370}
      priority
      onClick={onImageClick}
    />
  );

  return (
    <div
      className={twMerge('flex w-full justify-center', className)}
      style={{ background: getAdCssBackgroundColorProp(bgcolor) }}
    >
      {url ? (
        isUrlValidForInternalUsage(url) ? (
          <NextLink href={url} passHref>
            <Anchor
              className={twJoin(
                'mx-auto w-screen-sm items-center justify-center lg:w-[1264px]',
                mobileImage ? 'flex' : 'hidden lg:flex'
              )}
              onClick={onImageClick}
            >
              {ImageComponent}
            </Anchor>
          </NextLink>
        ) : (
          <Anchor
            className={twJoin(
              'mx-auto w-screen-sm items-center justify-center lg:w-[1264px]',
              mobileImage ? 'flex' : 'hidden lg:flex'
            )}
            href={url}
            rel="noopener noreferrer"
            target="_blank"
            onClick={onImageClick}
          >
            {ImageComponent}
          </Anchor>
        )
      ) : (
        ImageComponent
      )}
    </div>
  );
};

FeaturedImage.propTypes = {
  bgcolor: PropTypes.arrayOf(PropTypes.string),
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  description: PropTypes.string,
  gaEventCategory: PropTypes.string,
  image: PropTypes.string.isRequired,
  mobileImage: PropTypes.string,
  openWishlistOnClick: PropTypes.bool,
  url: PropTypes.string,
};

export default FeaturedImage;
